import { useMemo } from 'react';

import {
  PaymentSourceType,
  StripeCardStatus,
} from 'src/services/api/invoicesApi';
import { StripePaymentMethod } from 'src/types/payment';

type Props = {
  defaultSourceId: string;
  selectedSourceId?: string | null | undefined;
  sources: StripePaymentMethod[];
};

/**
 *
 * @param param0 {defaultSource, sources, isSourcesLoading}
 * @description Takes in the list of sources, default source and loading state
 * and plucks out the primary payment source from the list of sources.
 * @returns {selectedSource, primarySource, filteredSources}
 */
export const usePrimaryPaymentSource = ({
  defaultSourceId,
  selectedSourceId,
  sources = [],
}: Props) => {
  // Since we need sources for autocharge purposes, unverified bank accounts cannot be used.
  const validSources = useMemo(
    () =>
      sources.filter(
        (src) =>
          !(
            src.object === PaymentSourceType.BankAccount &&
            src.status !== StripeCardStatus.Verified
          ),
      ),
    [sources],
  );

  // Find primary source based on stripe customer's default source
  const primarySource = useMemo(
    () => validSources.find((s) => s.id === defaultSourceId),
    [defaultSourceId, validSources],
  );

  // Selected source represent that is manually selected by the user
  // If no source is selected, we use the primary source
  const selectedSource = useMemo(
    () =>
      selectedSourceId
        ? validSources.find((s) => s.id === selectedSourceId)
        : primarySource,
    [validSources, selectedSourceId, primarySource],
  );

  return {
    selectedSource,
    primarySource,
    filteredSources: validSources,
  };
};
